// Debug logging - only enabled in development
const DEBUG = import.meta.env.DEV;

/**
 * useNotifications Hook (v2 - Supabase Realtime)
 * 
 * Manages notification state with real-time database sync.
 * - Loads notifications from Supabase database
 * - Subscribes to Realtime INSERT/UPDATE events
 * - Handles reconnection with refetch
 * - Polls for notification generation (fees, deposits, etc.)
 * - Migrates localStorage notifications on first load
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import {
  AppNotification,
  DbNotification,
  mapDbToAppNotification,
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteAllNotifications,
  migrateLocalStorageNotifications,
  createCreatorFeesNotification,
  createFollowedCreatorLaunchNotification,
  lookupUsernames,
} from '@/lib/notifications';

const NOTIFICATIONS_OPTIMISTIC_EVENT = 'memelaunch:notifications:optimistic';
import { trackEvent } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { useCreatorFollows } from '@/hooks/useCreatorFollows';
import BN from 'bn.js';
import {
  FeeMetricsClient,
  hasClaimableCreatorFees,
  formatSolAmount,
} from '@/lib/meteora/feeMetricsClient';
import { getSolanaEndpoint } from '@/lib/solana/config';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { addBreadcrumb, Sentry } from '@/lib/sentry';
import { toast } from 'sonner';
import { NotificationToast } from '@/components/notifications/NotificationToast';
import { createElement } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useVisibilityReconnect } from '@/providers/VisibilityReconnectProvider';
import { WebSocketService } from '@/services/ws/WebSocketService';

// Polling intervals for deposit detection
// - When tracked by Helius: 60 seconds (webhook handles real-time, this is just fallback)
// - When not tracked: 30 seconds (fallback polling is primary detection method)
const POLL_INTERVAL_TRACKED_MS = 60 * 1000;
const POLL_INTERVAL_UNTRACKED_MS = 30 * 1000;

// Track last fee check to avoid spamming (once per 6 hours)
const LAST_FEE_CHECK_KEY = 'memelaunch_last_fee_check';
const FEE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

// Track last followed creator check (per creator, per day)
const LAST_CREATOR_CHECK_KEY = 'memelaunch_last_creator_check';

// Deposit detection
const LAST_BALANCE_KEY = 'memelaunch_last_balance';
const DEPOSIT_THRESHOLD = 0.001; // SOL minimum to avoid dust notifications

// Toast auto-dismiss duration (ms)
const TOAST_DURATION = 5000;

// WebSocket instant deposit detection
// Debounce for WS account change callbacks before checking balance
const WS_DEPOSIT_DEBOUNCE_MS = 300;
// Module-level cooldown shared across all hook instances.
// Solana can fire multiple accountChange events for a single deposit
// (token account reallocs, multi-instruction txs), so 2s cooldown
// prevents duplicate checks that the 300ms debounce alone cannot.
let lastWsDepositCheckMs = 0;
const WS_DEPOSIT_COOLDOWN_MS = 2000;

// Records the most recent WS-emitted deposit toast per wallet so the Realtime
// INSERT handler suppresses its own toast when the helius-webhook row arrives
// shortly after. Persistent dropdown row is still inserted; only the duplicate
// toast is suppressed. The webhook is the sole writer of the persistent row.
const lastWsDepositToastByWallet = new Map<string, { amountSol: number; ts: number }>();
const WS_TOAST_SUPPRESSION_WINDOW_MS = 10_000;

// Suppression for deposit detection during fee claims.
// When a fee claim lands, SOL increases in wallet. Without suppression,
// the deposit detector would misidentify this as an external deposit.
// Callers dispatch 'memelaunch:suppress-deposit-detection' before claiming.
let depositSuppressedUntilMs = 0;
const DEPOSIT_SUPPRESSION_DURATION_MS = 15_000;

// Notification types suppressed by the "Trades on my tokens" toggle.
// creator_pool_trade is not in the frontend NotificationType union but is
// written by the Helius webhook, so it arrives via Realtime as a string.
const TOKEN_TRADE_TYPES: string[] = [
  'creator_fees_claimable',
  'creator_fees_claimed',
  'creator_pool_trade',
];

// Notification types suppressed by the "New followers" toggle
const FOLLOWER_TYPES: string[] = [
  'new_follower',
];

// Check whether a notification should be surfaced (toast + unread badge)
// based on the user's notification preferences.
// Returns false when the master toggle is off, or when the notification's
// type falls under a disabled category toggle.
function shouldSurfaceNotification(
  type: string,
  notificationsEnabled: boolean,
  notifyOnTokenTrades: boolean,
  notifyOnNewFollowers: boolean,
): boolean {
  if (!notificationsEnabled) return false;
  if (!notifyOnTokenTrades && TOKEN_TRADE_TYPES.includes(type)) return false;
  if (!notifyOnNewFollowers && FOLLOWER_TYPES.includes(type)) return false;
  return true;
}

// Show a toast notification for money-related notifications.
// Uses a custom NotificationToast component (matches design system, has X close
// button, truncates long base58 addresses) instead of the bare Sonner string toast.
function showNotificationToast(notification: AppNotification) {
  // Only show toasts for money notifications (deposits, trades, withdrawals, fees)
  if (notification.category !== 'money') return;

  toast.custom(
    (t) =>
      createElement(NotificationToast, {
        notification,
        onDismiss: () => toast.dismiss(t),
      }),
    {
      duration: TOAST_DURATION,
      position: 'bottom-right',
    }
  );
}

export function useNotifications() {
  const { walletAddress, connected } = useTurnkeyWallet();
  const wallet = walletAddress || null;
  const { preferences } = useUserPreferences();

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isHeliusTracked, setIsHeliusTracked] = useState(false);

  const { following } = useCreatorFollows(wallet);
  const { wakeCounter } = useVisibilityReconnect();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const feeClientRef = useRef<FeeMetricsClient | null>(null);
  const realtimeChannelRef = useRef<RealtimeChannel | null>(null);
  const hasMigratedRef = useRef(false);
  const realtimeErrorCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeErrorReportedRef = useRef(false);

  // Only report Realtime errors to Sentry after this many consecutive failures
  const REALTIME_ERROR_THRESHOLD = 5;

  // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
  const getReconnectDelay = (errorCount: number): number => {
    return Math.min(1000 * Math.pow(2, errorCount - 1), 30000);
  };

  // Check if wallet is tracked by Helius for instant notifications
  useEffect(() => {
    if (!wallet) {
      setIsHeliusTracked(false);
      return;
    }

    // Check session storage first (fast path)
    const trackingKey = `helius_tracked_${wallet}`;
    if (sessionStorage.getItem(trackingKey)) {
      setIsHeliusTracked(true);
      return;
    }

    // Query database for tracking status
    supabase
      .from('creator_profiles')
      .select('helius_tracked')
      .eq('wallet_address', wallet)
      .maybeSingle()
      .then(({ data }) => {
        const tracked = data?.helius_tracked ?? false;
        setIsHeliusTracked(tracked);
        if (tracked) {
          sessionStorage.setItem(trackingKey, 'true');
        }
        DEBUG && console.log('[useNotifications] Helius tracking status:', tracked ? 'tracked' : 'not tracked');
      });
  }, [wallet]);
  
  // Initialize fee client
  useEffect(() => {
    feeClientRef.current = new FeeMetricsClient(getSolanaEndpoint());
  }, []);
  
  // Load notifications from database
  const loadNotifications = useCallback(async () => {
    if (!wallet) {
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }
    
    try {
      const data = await fetchNotifications(wallet);
      setNotifications(data);
      setUnreadCount(
        data.filter(n => !n.read && shouldSurfaceNotification(
          n.type,
          preferences.notificationsEnabled,
          preferences.notifyOnTokenTrades,
          preferences.notifyOnNewFollowers,
        )).length
      );
    } catch (err) {
      console.error('[useNotifications] Load error:', err);
      Sentry.captureException(err, {
        tags: { feature: 'notifications' },
        extra: { hook: 'useNotifications', action: 'load' },
      });
    } finally {
      setIsLoading(false);
    }
  }, [wallet, preferences.notificationsEnabled, preferences.notifyOnTokenTrades, preferences.notifyOnNewFollowers]);

  // Refetch notifications (for reconnection scenarios)
  const refetchNotifications = useCallback(async () => {
    DEBUG && console.log('[useNotifications] Refetching notifications...');
    await loadNotifications();
  }, [loadNotifications]);

  // Allow other parts of the app to request an immediate refresh (e.g. after a wallet-balance toast)
  useEffect(() => {
    if (!wallet) return;

    const handleRefresh = () => {
      DEBUG && console.log('[useNotifications] External refresh requested');
      refetchNotifications();
    };

    window.addEventListener('memelaunch:notifications:refresh', handleRefresh);
    return () => window.removeEventListener('memelaunch:notifications:refresh', handleRefresh);
  }, [wallet, refetchNotifications]);

  // Listen for optimistic notifications (instant bell update before DB round-trip)
  useEffect(() => {
    if (!wallet) return;

    const handleOptimistic = (event: Event) => {
      const notification = (event as CustomEvent<AppNotification>).detail;
      if (!notification) return;

      DEBUG && console.log('[useNotifications] Optimistic notification received:', notification.type);

      const shouldSurface = shouldSurfaceNotification(
        notification.type,
        preferences.notificationsEnabled,
        preferences.notifyOnTokenTrades,
        preferences.notifyOnNewFollowers,
      );

      setNotifications(prev => {
        // Deduplicate by dedupKey to avoid double-adding
        if (notification.dedupKey && prev.some(n => n.dedupKey === notification.dedupKey)) return prev;
        // Also check by id
        if (prev.some(n => n.id === notification.id)) return prev;
        return [notification, ...prev];
      });
      if (shouldSurface) {
        setUnreadCount(prev => prev + 1);
        showNotificationToast(notification);
      }
    };

    window.addEventListener(NOTIFICATIONS_OPTIMISTIC_EVENT, handleOptimistic);
    return () => window.removeEventListener(NOTIFICATIONS_OPTIMISTIC_EVENT, handleOptimistic);
  }, [wallet, preferences.notificationsEnabled, preferences.notifyOnTokenTrades, preferences.notifyOnNewFollowers]);

  // Subscribe to Realtime updates
  useEffect(() => {
    if (!wallet || !connected) {
      // Cleanup existing subscription
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      return;
    }

    // Reset error count on wake so stale counts don't block re-subscription
    realtimeErrorCountRef.current = 0;

    DEBUG && console.log('[useNotifications] Setting up Realtime subscription for:', wallet.slice(0, 8));
    
    const channel = supabase
      .channel(`notifications:${wallet}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `wallet_address=eq.${wallet}`,
        },
        (payload) => {
          const dbNotification = payload.new as DbNotification;
          const createdAt = new Date(dbNotification.created_at).getTime();
          const latencyMs = Date.now() - createdAt;

          DEBUG && console.log('[useNotifications] Realtime INSERT:', dbNotification.type);
          DEBUG && console.log(`[useNotifications] Notification latency: ${latencyMs}ms`);

          addBreadcrumb('notification.realtime.insert', 'notification', {
            type: dbNotification.type,
            latency_ms: latencyMs,
          });

          const newNotification = mapDbToAppNotification(dbNotification);

          // Dedup-aware: check by id AND dedupKey (to reconcile optimistic entries)
          setNotifications(prev => {
            if (prev.some(n => n.id === newNotification.id)) return prev;

            // If an optimistic notification exists with the same dedupKey, replace it
            // with the real DB version (don't increment unreadCount since it was already counted)
            const dedupKey = newNotification.dedupKey;
            if (dedupKey) {
              const optimisticIdx = prev.findIndex(
                n => n.dedupKey === dedupKey && n.id.startsWith('optimistic_')
              );
              if (optimisticIdx !== -1) {
                const updated = [...prev];
                // Preserve read status if user already dismissed the optimistic notification
                const preservedRead = prev[optimisticIdx].read;
                updated[optimisticIdx] = preservedRead
                  ? { ...newNotification, read: true }
                  : newNotification;
                return updated;
              }
            }

            // No optimistic match: genuinely new notification
            const surface = shouldSurfaceNotification(
              newNotification.type,
              preferences.notificationsEnabled,
              preferences.notifyOnTokenTrades,
              preferences.notifyOnNewFollowers,
            );
            if (surface) {
              setUnreadCount(c => c + 1);

              // Suppress toast if a WS deposit toast already fired for this
              // approximate event. Tolerance: same wallet, within the
              // suppression window, amount within 0.0001 SOL (handles the
              // sub-lamport mismatch between balance-diff and Helius transfer.amount).
              const wsRecent = lastWsDepositToastByWallet.get(wallet);
              const suppressToast =
                newNotification.type === 'deposit_received' &&
                wsRecent !== undefined &&
                Date.now() - wsRecent.ts < WS_TOAST_SUPPRESSION_WINDOW_MS &&
                Math.abs((newNotification.value ?? 0) - wsRecent.amountSol) < 0.0001;

              if (suppressToast) {
                lastWsDepositToastByWallet.delete(wallet);
              } else {
                showNotificationToast(newNotification);
              }
            }
            return [newNotification, ...prev];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `wallet_address=eq.${wallet}`,
        },
        (payload) => {
          DEBUG && console.log('[useNotifications] Realtime UPDATE:', payload.new);
          addBreadcrumb('notification.realtime.update', 'notification', { 
            id: (payload.new as DbNotification).id,
            read: (payload.new as DbNotification).read,
          });
          
          const updatedNotification = mapDbToAppNotification(payload.new as DbNotification);
          setNotifications(prev =>
            prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
          );
          
          // Update unread count if read status changed
          const oldRead = (payload.old as DbNotification)?.read;
          const newRead = (payload.new as DbNotification).read;
          if (!oldRead && newRead) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          } else if (oldRead && !newRead) {
            setUnreadCount(prev => prev + 1);
          }
        }
      )
      .subscribe((status) => {
        DEBUG && console.log('[useNotifications] Realtime status:', status);
        if (status === 'SUBSCRIBED') {
          DEBUG && console.log('[useNotifications] Realtime subscribed successfully');
          // Reset error count on successful subscription
          realtimeErrorCountRef.current = 0;
        }
        if (status === 'CHANNEL_ERROR') {
          realtimeErrorCountRef.current++;
          const errorCount = realtimeErrorCountRef.current;
          const delay = getReconnectDelay(errorCount);

          console.warn(`[useNotifications] Realtime channel error (attempt ${errorCount}, retry in ${delay}ms)`);

          // Report once per session when hitting the threshold
          // Single disconnects are expected (network changes, sleep/wake, etc.)
          if (errorCount === REALTIME_ERROR_THRESHOLD && !realtimeErrorReportedRef.current) {
            realtimeErrorReportedRef.current = true;
            addBreadcrumb('notification.realtime.persistent_error', 'notification', {
              error_count: errorCount,
              reconnect_delay_ms: delay,
              page: window.location.pathname,
            });
            Sentry.captureMessage('Notification Realtime channel error (persistent)', {
              level: 'warning',
              tags: {
                feature: 'notifications',
                error_type: 'realtime_disconnect',
                browser: navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop',
              },
              extra: {
                hook: 'useNotifications',
                wallet_prefix: wallet.slice(0, 8),
                error_count: errorCount,
                page: window.location.pathname,
                online: navigator.onLine,
                visibility: document.visibilityState,
              },
            });
          }

          // Clear any pending reconnect
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }

          // Refetch with exponential backoff
          reconnectTimeoutRef.current = setTimeout(() => {
            refetchNotifications();
          }, delay);
        }
        if (status === 'TIMED_OUT') {
          console.warn('[useNotifications] Realtime timed out, refetching...');
          refetchNotifications();
        }
      });
    
    realtimeChannelRef.current = channel;
    
    return () => {
      DEBUG && console.log('[useNotifications] Cleaning up Realtime subscription');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      // Reset error count so remounts start fresh
      realtimeErrorCountRef.current = 0;
    };
  }, [wallet, connected, refetchNotifications, wakeCounter, preferences.notificationsEnabled, preferences.notifyOnTokenTrades, preferences.notifyOnNewFollowers]);

  // Listen for deposit suppression events (fired before fee claims)
  useEffect(() => {
    const handleSuppress = () => {
      depositSuppressedUntilMs = Date.now() + DEPOSIT_SUPPRESSION_DURATION_MS;
      DEBUG && console.log('[useNotifications] Deposit detection suppressed for 15s (fee claim in progress)');
    };
    window.addEventListener('memelaunch:suppress-deposit-detection', handleSuppress);
    return () => window.removeEventListener('memelaunch:suppress-deposit-detection', handleSuppress);
  }, []);

  // WebSocket-driven instant deposit detection (~500ms vs 30-60s polling)
  // Uses Solana accountSubscribe to detect balance changes in near real-time.
  // This runs globally (NotificationDropdown is always mounted in TopBar).
  const wsDepositDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsPrevBalanceRef = useRef<number | null>(null);
  const wsInitialFetchRef = useRef(true);

  useEffect(() => {
    if (!wallet || !connected) return;

    const service = WebSocketService.getInstance();

    // Initialize previous balance from localStorage
    const storedBalance = localStorage.getItem(`${LAST_BALANCE_KEY}_${wallet}`);
    if (storedBalance) {
      wsPrevBalanceRef.current = parseFloat(storedBalance);
      wsInitialFetchRef.current = false;
    } else {
      wsPrevBalanceRef.current = null;
      wsInitialFetchRef.current = true;
    }

    const unsub = service.subscribeAccount(wallet, () => {
      // Debounce rapid-fire WS callbacks (e.g. multiple account changes in one tx)
      if (wsDepositDebounceRef.current) clearTimeout(wsDepositDebounceRef.current);
      wsDepositDebounceRef.current = setTimeout(async () => {
        // Module-level cooldown: prevent duplicate checks across hook instances
        // and burst accountChange events from multi-instruction txs
        const now = Date.now();
        if (now - lastWsDepositCheckMs < WS_DEPOSIT_COOLDOWN_MS) return;
        lastWsDepositCheckMs = now;

        try {
          const connection = new Connection(getSolanaEndpoint(), 'confirmed');
          const currentLamports = await connection.getBalance(new PublicKey(wallet));
          const currentSol = currentLamports / LAMPORTS_PER_SOL;

          // First run: store balance, skip notification
          if (wsInitialFetchRef.current || wsPrevBalanceRef.current === null) {
            wsPrevBalanceRef.current = currentSol;
            wsInitialFetchRef.current = false;
            localStorage.setItem(`${LAST_BALANCE_KEY}_${wallet}`, currentSol.toString());
            return;
          }

          const diff = currentSol - wsPrevBalanceRef.current;

          if (diff > DEPOSIT_THRESHOLD) {
            // Skip if deposit detection is suppressed (fee claim in progress)
            if (Date.now() < depositSuppressedUntilMs) {
              DEBUG && console.log(`[useNotifications] WS balance increase suppressed (fee claim): +${diff.toFixed(4)} SOL`);
              wsPrevBalanceRef.current = currentSol;
              localStorage.setItem(`${LAST_BALANCE_KEY}_${wallet}`, currentSol.toString());
              return;
            }

            DEBUG && console.log(`[useNotifications] WS deposit toast: +${diff.toFixed(4)} SOL`);

            // Ephemeral toast only. The helius-webhook is the sole writer of
            // the persistent dropdown row; when its Realtime INSERT arrives,
            // the handler at the top of this hook checks
            // lastWsDepositToastByWallet and skips the duplicate toast.
            if (preferences.notificationsEnabled) {
              showNotificationToast({
                id: `ws_toast_${now}`,
                type: 'deposit_received',
                category: 'money',
                title: 'Deposit Received',
                message: `${diff.toFixed(4)} SOL received`,
                timestamp: new Date(),
                read: false,
                cta: { label: 'View Portfolio', path: '/portfolio?tab=activity' },
                value: diff,
              });
              lastWsDepositToastByWallet.set(wallet, { amountSol: diff, ts: now });
            }

            addBreadcrumb('notification.ws_deposit_toast', 'notification', {
              type: 'deposit_received',
              amount: diff,
              wallet_prefix: wallet.slice(0, 8),
            });
          }

          // Update stored balance so polling path sees no diff
          wsPrevBalanceRef.current = currentSol;
          localStorage.setItem(`${LAST_BALANCE_KEY}_${wallet}`, currentSol.toString());
        } catch (err) {
          DEBUG && console.error('[useNotifications] WS deposit check error:', err);
        }
      }, WS_DEPOSIT_DEBOUNCE_MS);
    });

    return () => {
      unsub();
      if (wsDepositDebounceRef.current) {
        clearTimeout(wsDepositDebounceRef.current);
        wsDepositDebounceRef.current = null;
      }
      // Drop the prior wallet's toast-suppression entry so a deposit on a
      // newly connected wallet always surfaces both the WS toast and the
      // Realtime dropdown toast, even if amounts collide within the window.
      lastWsDepositToastByWallet.delete(wallet);
    };
  }, [wallet, connected, preferences.notificationsEnabled]);

  // Check for creator fees (money notification)
  const checkCreatorFees = useCallback(async () => {
    if (!wallet || !connected || !feeClientRef.current) return;

    // Rate limit: once per 6 hours
    const lastCheck = localStorage.getItem(`${LAST_FEE_CHECK_KEY}_${wallet}`);
    if (lastCheck && Date.now() - parseInt(lastCheck) < FEE_CHECK_INTERVAL_MS) {
      return;
    }

    try {
      // Fetch creator's tokens
      const { data: tokens } = await supabase
        .from('launched_tokens')
        .select('pool_address')
        .eq('creator_address', wallet)
        .not('pool_address', 'is', null);

      if (!tokens || tokens.length === 0) return;

      let totalClaimable = new BN(0);
      let claimableCount = 0;

      // Check each pool for claimable fees (limit to 5 to avoid spam)
      const poolsToCheck = tokens.slice(0, 5);
      for (const token of poolsToCheck) {
        if (!token.pool_address) continue;
        try {
          const metrics = await feeClientRef.current.fetchPoolFeeMetrics(
            new PublicKey(token.pool_address)
          );
          if (metrics && hasClaimableCreatorFees(metrics)) {
            totalClaimable = totalClaimable.add(metrics.creatorQuoteFee);
            claimableCount++;
          }
        } catch {
          // Skip failed pools
        }
      }

      // Create notification if there are claimable fees
      if (claimableCount > 0) {
        const solAmount = parseFloat(formatSolAmount(totalClaimable));
        if (solAmount > 0.0001) {
          const created = await createCreatorFeesNotification(wallet, solAmount, claimableCount);
          // Ensure badge updates instantly even if Realtime delivery is delayed
          if (created) {
            await refetchNotifications();
          }
        }
      }

      localStorage.setItem(`${LAST_FEE_CHECK_KEY}_${wallet}`, Date.now().toString());
    } catch (err) {
      console.error('[useNotifications] Error checking creator fees:', err);
    }
  }, [wallet, connected, refetchNotifications]);

  // Check for new tokens from followed creators (social notification)
  const checkFollowedCreatorLaunches = useCallback(async () => {
    if (!wallet || !connected || following.length === 0) return;

    try {
      let createdAny = false;

      // Get last check timestamp per creator
      const lastChecksStr = localStorage.getItem(`${LAST_CREATOR_CHECK_KEY}_${wallet}`);
      const lastChecks: Record<string, number> = lastChecksStr ? JSON.parse(lastChecksStr) : {};

      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      // Batch-fetch usernames for all followed creators
      const creatorsToCheck = following.slice(0, 10);
      const usernameMap = await lookupUsernames(creatorsToCheck);

      // Check each followed creator for recent launches
      for (const creatorAddress of creatorsToCheck) {
        const lastCheck = lastChecks[creatorAddress];
        const checkFrom = lastCheck
          ? new Date(lastCheck).toISOString()
          : oneDayAgo;

        const { data: tokens } = await supabase
          .from('launched_tokens')
          .select('*')
          .eq('creator_address', creatorAddress)
          .gte('created_at', checkFrom)
          .order('created_at', { ascending: false })
          .limit(3);

        if (tokens && tokens.length > 0) {
          for (const token of tokens) {
            const creatorName = usernameMap.get(creatorAddress) || 'Unknown';

            const created = await createFollowedCreatorLaunchNotification(
              wallet,
              creatorAddress,
              creatorName,
              token.mint_address,
              token.name,
              token.symbol,
              token.image_url
            );

            if (created) createdAny = true;
          }
        }

        // Update last check timestamp
        lastChecks[creatorAddress] = Date.now();
      }

      localStorage.setItem(`${LAST_CREATOR_CHECK_KEY}_${wallet}`, JSON.stringify(lastChecks));

      // Ensure badge updates instantly even if Realtime delivery is delayed
      if (createdAny) {
        await refetchNotifications();
      }
    } catch (err) {
      console.error('[useNotifications] Error checking followed creator launches:', err);
    }
  }, [wallet, connected, following, refetchNotifications]);

  // Generate notifications (called on load and interval).
  // Deposit/withdrawal notifications are emitted by the helius-webhook only;
  // no client-side polling fallback (it produced duplicate rows with synthetic
  // tx signatures that could not dedupe against Helius's real-sig rows).
  const generateNotifications = useCallback(async () => {
    if (!wallet || !connected) return;

    await Promise.allSettled([
      checkCreatorFees(),
      checkFollowedCreatorLaunches(),
    ]);
  }, [wallet, connected, checkCreatorFees, checkFollowedCreatorLaunches]);
  
  // Mark single notification as read
  const markRead = useCallback(async (id: string) => {
    if (!wallet) return;
    
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    const success = await markNotificationRead(id, wallet);
    if (!success) {
      // Revert on failure
      loadNotifications();
    }
    
    trackEvent('notification_click', { id });
  }, [wallet, loadNotifications]);
  
  // Mark all as read
  const markAllRead = useCallback(async () => {
    if (!wallet) return;
    
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    const success = await markAllNotificationsRead(wallet);
    if (!success) {
      // Revert on failure
      loadNotifications();
    }
  }, [wallet, loadNotifications]);
  
  // Clear all notifications
  const clearAll = useCallback(async () => {
    if (!wallet) return;

    // Optimistic update
    setNotifications([]);
    setUnreadCount(0);

    const success = await deleteAllNotifications(wallet);
    if (!success) {
      // Revert on failure
      loadNotifications();
    }
  }, [wallet, loadNotifications]);

  // Track impression
  const trackImpression = useCallback((id: string) => {
    trackEvent('notification_impression', { id });
  }, []);
  
  // Initial load, migration, and wallet change
  useEffect(() => {
    const initialize = async () => {
      if (!wallet) {
        setNotifications([]);
        setUnreadCount(0);
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      // Migrate localStorage notifications (once per wallet)
      if (!hasMigratedRef.current) {
        hasMigratedRef.current = true;
        try {
          await migrateLocalStorageNotifications(wallet);
        } catch (err) {
          console.error('[useNotifications] Migration error:', err);
        }
      }
      
      // Load from database
      await loadNotifications();
    };
    
    initialize();
  }, [wallet, loadNotifications]);
  
  // Start polling for generation when wallet is connected
  // Use longer interval if wallet is tracked by Helius (webhook handles real-time)
  useEffect(() => {
    if (!wallet || !connected) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const pollInterval = isHeliusTracked ? POLL_INTERVAL_TRACKED_MS : POLL_INTERVAL_UNTRACKED_MS;
    DEBUG && console.log(`[useNotifications] Polling interval: ${pollInterval / 1000}s (tracked: ${isHeliusTracked})`);

    // Initial generation (delayed to avoid blocking initial load)
    const initialTimeout = setTimeout(() => {
      generateNotifications();
    }, 2000);

    // Start polling
    pollIntervalRef.current = setInterval(() => {
      generateNotifications();
    }, pollInterval);

    return () => {
      clearTimeout(initialTimeout);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [wallet, connected, isHeliusTracked, generateNotifications]);
  
  // Categorized notifications - sort by timestamp descending
  const moneyNotifications = notifications
    .filter(n => n.category === 'money')
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const socialNotifications = notifications
    .filter(n => n.category === 'social')
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  
  return {
    notifications,
    moneyNotifications,
    socialNotifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    clearAll,
    trackImpression,
    refresh: loadNotifications,
    generate: generateNotifications,
  };
}
